import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle,
  AlertCircle,
  RefreshCw,
  GraduationCap,
  School,
  BookOpen,
  ArrowRight
} from 'lucide-react';

import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  getUserCourses
} from '../../firebase/collections';
import { formatTimestamp } from '../../utils/timestampUtils';
import { checkAndCleanupExpiredTodos } from '../../utils/cleanupExpiredTodos';
import { setupPeriodicCleanup } from '../../utils/scheduledCleanup';
import { logDailyActivity, getStreakStats, StudyStreakManager } from '../../utils/studyStreakManager';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import DailyTodo from '../../components/DailyTodo';
import MotivationalQuote from '../../components/MotivationalQuote';
import StudyCounseling from '../../components/StudyCounseling';
import StudyStreakCard from '../../components/StudyStreakCard';
import BatchEnrollmentButton from '../../components/batch/BatchEnrollmentButton';
import CoachingDashboardCard from '../../components/coaching/CoachingDashboardCard';
import TeacherDashboard from '../teacher/TeacherDashboard';
import InstituteDashboard from '../institute/InstituteDashboard';
import ForceTeacherRedirect from '../../components/ForceTeacherRedirect';

const Dashboard = () => {
  const { userData, isConnected, connectionStatus, currentUser, userProfile } = useRealtime();
  const { logActivity, ACTIVITY_TYPES } = useActivityLogger(currentUser?.uid);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTests: 0,
    totalCourses: 0,
    averageAccuracy: 0,
    studyStreak: 0,
    totalNotes: 0,
    totalDoubts: 0,
    totalResources: 0,
    recentActivity: []
  });
  const [recentNotes, setRecentNotes] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Quick actions removed as requested

  // Use real-time data from context
  useEffect(() => {
    if (isConnected && userData && currentUser?.uid) {
      // Log daily activity for streak tracking
      logActivity(ACTIVITY_TYPES.DASHBOARD_VISIT);
      
      // Calculate stats from real-time data
      const testResults = userData.testResults || [];
      const notes = userData.notes || [];
      const doubts = userData.doubts || [];
      const activities = userData.activities || [];
      
      // Load streak data
      const loadStreakData = async () => {
        try {
          const streakStats = await getStreakStats(currentUser.uid);
          
          // Calculate statistics
          const calculatedStats = {
            totalTests: testResults.length,
            totalCourses: userData.userCourses?.length || userData.courses?.length || 0,
            averageAccuracy: testResults.length > 0 
              ? Math.round(testResults.reduce((sum, test) => sum + (test.accuracy || 0), 0) / testResults.length)
              : 0,
            studyStreak: streakStats.currentStreak, // Use new streak system
            totalNotes: notes.length,
            totalDoubts: doubts.length,
            totalResources: userData.resources?.length || 0,
            recentActivity: activities.slice(0, 5)
          };
          
          setStats(calculatedStats);
          setRecentNotes(notes.slice(0, 5));
          setRecentActivity(activities.slice(0, 10));
          setLastUpdated(new Date());
          setLoading(false);
        } catch (error) {
          // Error loading streak data
          // Fallback to old calculation
          const calculatedStats = {
            totalTests: testResults.length,
            totalCourses: userData.userCourses?.length || userData.courses?.length || 0,
            averageAccuracy: testResults.length > 0 
              ? Math.round(testResults.reduce((sum, test) => sum + (test.accuracy || 0), 0) / testResults.length)
              : 0,
            studyStreak: StudyStreakManager.calculateStudyStreak(activities),
            totalNotes: notes.length,
            totalDoubts: doubts.length,
            totalResources: userData.resources?.length || 0,
            recentActivity: activities.slice(0, 5)
          };
          
          setStats(calculatedStats);
          setRecentNotes(notes.slice(0, 5));
          setRecentActivity(activities.slice(0, 10));
          setLastUpdated(new Date());
          setLoading(false);
        }
      };
      
      loadStreakData();
    }
  }, [isConnected, userData, currentUser]);

  // Check and cleanup expired todos on app startup
  useEffect(() => {
    const cleanupTodos = async () => {
      try {
        const result = await checkAndCleanupExpiredTodos();
        // Cleanup completed silently
      } catch (error) {
        // Handle error silently
      }
    };
    
    cleanupTodos();
  }, []);

  // Setup periodic cleanup
  useEffect(() => {
    const cleanupInterval = setupPeriodicCleanup();
    
    // Cleanup on component unmount
    return () => {
      cleanupInterval();
    };
  }, []);



  const handleRefresh = () => {
    setLastUpdated(new Date());
  };

  // Loading state
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Connecting to your data...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Status: {connectionStatus}</p>
        </div>
      </div>
    );
  }

  // Role-based dashboard rendering
  // This component should only render for students
  // Teachers, institutes, and admins are redirected by RoleBasedRedirect
  // If they somehow reach here, show a loading state while redirect happens
  if (userData?.role === 'teacher' || userData?.userType === 'teacher' || userProfile?.role === 'teacher') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Redirecting to Teacher Dashboard...</p>
        </div>
      </div>
    );
  }

  if (userData?.role === 'institute' || userData?.userType === 'institute' || userProfile?.role === 'institute') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Redirecting to Institute Dashboard...</p>
        </div>
      </div>
    );
  }

  if (userData?.role === 'coaching_admin' || userProfile?.role === 'coaching_admin') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Redirecting to Coaching Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Force redirect failsafe for teachers */}
      <ForceTeacherRedirect />
      


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 pt-2">
        {/* Batch Enrollment Button */}
        <BatchEnrollmentButton userId={currentUser?.uid} />

        {/* Admissions Quick Access */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-4 sm:mb-6"
        >
          <Link
            to="/admissions"
            className="block bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
          >
            <div className="p-4 sm:p-6 flex items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                <div className="bg-white/20 p-2.5 sm:p-3 rounded-lg group-hover:bg-white/30 transition-colors flex-shrink-0">
                  <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-xl font-bold mb-1 truncate">Explore Admissions</h3>
                  <p className="text-blue-100 text-xs sm:text-sm line-clamp-2">
                    Browse schools, colleges, and coaching centers. Apply now!
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 group-hover:translate-x-2 transition-transform" />
            </div>
          </Link>
        </motion.div>

        {/* Enhanced Header Section */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 px-1"
          >
            Welcome back, {currentUser?.displayName?.split(' ')[0] || 'Student'}! 👋
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-400 px-1"
          >
            Ready to continue your learning journey? Here's what's happening today.
          </motion.p>
        </div>

        {/* Top Row - Study Streak and Motivation with responsive heights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
          {/* Study Streak - Responsive height */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="group relative min-h-[320px] sm:min-h-[380px] md:min-h-[420px] lg:h-[480px]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-400/10 to-blue-500/10 rounded-lg sm:rounded-xl lg:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg sm:shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl transition-all duration-500 h-full">
              <StudyStreakCard userId={currentUser?.uid} />
            </div>
          </motion.div>

          {/* Motivational Quote - Responsive height */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="group relative min-h-[320px] sm:min-h-[380px] md:min-h-[420px] lg:h-[480px]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-pink-500/10 rounded-lg sm:rounded-xl lg:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg sm:shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl transition-all duration-500 h-full">
              <MotivationalQuote />
            </div>
          </motion.div>
        </div>

        {/* Middle Row - Coaching Dashboard (if applicable) */}
        <CoachingDashboardCard userProfile={userData} />

        {/* Bottom Row - Today Task and MahStry counselling with responsive heights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-4 sm:mt-6 lg:mt-8">
          {/* Daily Todo List - Responsive height */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="group relative min-h-[320px] sm:min-h-[380px] md:min-h-[420px] lg:h-[480px]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-indigo-500/10 rounded-lg sm:rounded-xl lg:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg sm:shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl transition-all duration-500 h-full">
              <DailyTodo userId={currentUser?.uid} />
            </div>
          </motion.div>

          {/* MahStry counselling - Responsive height */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="group relative min-h-[320px] sm:min-h-[380px] md:min-h-[420px] lg:h-[480px]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-violet-500/10 rounded-lg sm:rounded-xl lg:rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg sm:shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl transition-all duration-500 h-full">
              <StudyCounseling 
                userId={currentUser?.uid} 
                userName={currentUser?.displayName || currentUser?.email || 'Student'} 
              />
            </div>
          </motion.div>
        </div>

        {/* Additional Stats section removed as requested */}
      </div>
      

    </div>
  );
};

export default Dashboard;
